import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import { Search, Trash2, Mail, Phone, Building2, Users } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUSES = ["New", "Contacted", "Qualified", "Won", "Lost"];

export default function AdminLeads() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");

    const load = async () => {
        try { setList(await api.get("/admin/leads")); } catch { toast.error("Load failed"); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => list.filter((l) => {
        if (statusFilter !== "All" && (l.status || "New") !== statusFilter) return false;
        if (query && !((l.name || "") + (l.email || "") + (l.company || "")).toLowerCase().includes(query.toLowerCase())) return false;
        return true;
    }), [list, query, statusFilter]);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/leads/${id}`, { status });
            toast.success("Status updated");
            setList((s) => s.map((x) => x.id === id ? { ...x, status } : x));
        } catch { toast.error("Failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this lead?")) return;
        try { await api.del(`/admin/leads/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Failed"); }
    };

    const exportCSV = () => {
        const header = ["id", "company", "name", "email", "phone", "cohort_size", "interest", "status", "created_at"];
        const rows = filtered.map((c) => header.map((h) => `"${(c[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
        const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `leads_${Date.now()}.csv`; a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV exported");
    };

    return (
        <AppLayout searchPlaceholder="Search leads...">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Corporate Leads</h1>
                    <p className="text-sm text-gray-400 mt-2">{list.length} leads from the corporate-training form</p>
                </div>
                <button data-testid="export-leads" onClick={exportCSV} className="px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">Export CSV</button>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input data-testid="search-leads" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by name, email or company..." className="w-full bg-ink-700 border border-white/5 rounded-full pl-11 pr-5 py-3 text-sm focus:outline-none focus:border-lime/40" />
                </div>
                {["All", ...STATUSES].map((f) => (
                    <button key={f} onClick={() => setStatusFilter(f)} data-testid={`leads-filter-${f.toLowerCase()}`} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${statusFilter === f ? "bg-white text-ink-900" : "text-gray-400 hover:text-white"}`}>{f}</button>
                ))}
            </div>

            {loading ? <div className="text-gray-500">Loading…</div> : list.length === 0 ? (
                <div className="bg-ink-700 border border-white/5 rounded-3xl p-12 text-center">
                    <Users className="w-10 h-10 text-gray-600 mx-auto mb-4" />
                    <h3 className="font-display font-bold text-xl">No leads yet</h3>
                    <p className="text-sm text-gray-400 mt-2">Submissions from the homepage corporate-training form will appear here.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((l) => (
                        <div key={l.id} data-testid={`lead-card-${l.id}`} className="bg-ink-700 border border-white/5 rounded-3xl p-5 hover:border-white/15 transition-colors">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-GB") : ""}</span>
                                <Select value={l.status || "New"} onValueChange={(v) => updateStatus(l.id, v)}>
                                    <SelectTrigger data-testid={`lead-status-${l.id}`} className="h-8 px-3 bg-ink-800 border-white/5 rounded-full text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-ink-800 border-white/10 text-white">
                                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-ink-700 focus:text-white">{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Building2 className="w-4 h-4 text-grape" /> {l.company}</h3>
                            <div className="text-sm font-semibold mt-1">{l.name}</div>
                            <a href={`mailto:${l.email}`} className="text-xs text-gray-400 hover:text-lime inline-flex items-center gap-1.5 mt-2"><Mail className="w-3 h-3" /> {l.email}</a>
                            {l.phone && <div className="text-xs text-gray-400 inline-flex items-center gap-1.5 mt-1"><Phone className="w-3 h-3" /> {l.phone}</div>}
                            <div className="mt-3 flex flex-wrap gap-2">
                                <span className="px-2 py-1 rounded-full bg-grape/15 text-grape text-[10px] font-bold">Cohort: {l.cohort_size || "—"}</span>
                                {l.interest && <span className="px-2 py-1 rounded-full bg-lime/15 text-lime text-[10px] font-bold">{l.interest}</span>}
                            </div>
                            {l.message && <p className="text-xs text-gray-500 mt-3 line-clamp-3">{l.message}</p>}
                            <div className="mt-4 pt-4 border-t border-white/5 flex justify-end">
                                <button data-testid={`delete-lead-${l.id}`} onClick={() => remove(l.id)} className="w-8 h-8 rounded-full hover:bg-red-500/20 hover:text-red-400 text-gray-500 inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AppLayout>
    );
}
